/**
 * useInstanceDetails - Hook para buscar detalhes consolidados de uma instância
 *
 * ARQUITETURA:
 * - 100% server-side via RPC `get_instance_details_safe`
 * - Retorna: summary (resumo), timeline (auditoria) e billing (cobrança)
 * - Padrão Tenant v2.0 (tenant validado no backend via p_tenant_hint)
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useInstanceDetails');

/** Item de timeline de auditoria da instância */
export interface InstanceTimelineEvent {
  id: string;
  action_type: string;
  old_status: string | null;
  new_status: string | null;
  performed_by_type: string | null;
  performed_by: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** Item de histórico de cobrança/assinatura */
export interface InstanceBillingEvent {
  id: string;
  action_type: string;
  notes: string | null;
  performed_by: string | null;
  had_stripe: boolean;
  created_at: string;
}

/** Resumo da instância */
export interface InstanceSummary {
  id: string;
  instance_name: string;
  instance_code: string;
  status: string;
  payment_status: string | null;
  source_type: string;
  channel_type: string;
  provider_display_name: string;
  provider_logo_url: string | null;
  created_at: string;
  requested_at: string | null;
  released_at: string | null;
  activated_at: string | null;
  inbox_id_masked: string | null;
  inbox_name: string | null;
  inbox_status: string | null;
  addon_name: string | null;
  has_subscription: boolean;
  has_tenant_addon: boolean;
  addon_status: string | null;
  addon_cancelled_at: string | null;
  addon_expires_at: string | null;
}

/** Payload completo retornado pela RPC */
export interface InstanceDetailsPayload {
  summary: InstanceSummary | null;
  timeline: InstanceTimelineEvent[];
  billing: InstanceBillingEvent[];
}

/**
 * Hook para buscar detalhes de uma instância (auditoria/suporte)
 */
export function useInstanceDetails(
  instanceId: string | null,
  tenantId: string | null,
  enabled: boolean = true,
) {
  return useQuery<InstanceDetailsPayload>({
    queryKey: ['instance-details', instanceId, tenantId],
    enabled: enabled && !!instanceId && !!tenantId,
    queryFn: async () => {
      logger.info('Buscando detalhes da instância', { instanceId, tenantId });

      const { data, error } = await supabase.rpc('get_instance_details_safe', {
        p_instance_id: instanceId!,
        p_tenant_hint: tenantId!,
      });

      if (error) {
        logger.error('Erro ao buscar detalhes da instância', { error });
        throw error;
      }

      return (data as unknown as InstanceDetailsPayload) ?? {
        summary: null,
        timeline: [],
        billing: [],
      };
    },
    staleTime: 30 * 1000, // 30s — auditoria não muda com frequência
  });
}
